import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Select, MenuItem, FormControl, FormHelperText } from "@mui/material";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";
import DynamicAutocomplete from "@/components/common/inputs/select/DynamicAutocomplete";

interface InviteUserProps {
  onClose: () => void;
  onSubmit: () => void;
  isModalOpen: boolean;
}

interface UserType {
  id: number;
  user_type: string;
}

interface InviteUserValues {
  email: string;
  userType: number | null;
}

const UserManagementInvite: React.FC<InviteUserProps> = ({
  onClose,
  onSubmit,
  isModalOpen,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUserTypes, setIsLoadingUserTypes] = useState(false);
  const [userTypes, setUserTypes] = useState<UserType[]>([]);

  useEffect(() => {
    const fetchUserTypes = async () => {
      try {
        setIsLoadingUserTypes(true);
        const { authenticatedCall } = actionsHandler("userTypeList");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
          }),
        );

        if (response.ok) {
          setUserTypes(
            response.data.filter((item: any) => item.id !== 3 && item.id !== 4),
          );
        } else {
          toast.error(response.data?.message || "Failed to fetch user types");
        }
      } catch (error) {
        console.error("Error fetching user types:", error);
      } finally {
        setIsLoadingUserTypes(false);
      }
    };

    fetchUserTypes();
  }, [dispatch]);

  const validationSchema = Yup.object({
    email: Yup.string()
      .required("Email is required")
      .email("Please enter a valid email address"),
    userType: Yup.number().required("Please select a user type"),
  });

  const handleSubmit = async (values: InviteUserValues) => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("inviteUser");

      const response = await dispatch(
        authenticatedCall({
          payload: {
            email: values.email,
            user_type: values.userType,
          },
        }),
      );

      if (response.ok) {
        toast.success(response.data?.message || "User invited successfully");
        onSubmit();
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      userType: null,
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <div
      className={`tw-w-[350px] tw-fixed tw-bg-black/25 tw-top-0 tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-z-50 tw-transition-all tw-ease-in-out tw-duration-1000 ${
        isModalOpen ? "tw-right-0" : "tw-right-[-265px]"
      }`}
    >
      <div className="tw-bg-white tw-shadow-lg tw-mx-auto tw-h-screen tw-w-[350px] tw-flex tw-flex-col">
        <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b">
          <h2 className="tw-text-base tw-font-semibold">Invite Stakeholder</h2>
          <button onClick={onClose}>
            <img src={CloseIcon} alt="Close" className="tw-w-auto tw-h-auto" />
          </button>
        </div>

        <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-4">
          <form
            onSubmit={formik.handleSubmit}
            className="tw-h-full tw-flex tw-flex-col"
          >
            <div className="tw-flex-1">
              <div className="tw-mb-6">
                <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter email address"
                  className={`tw-w-full tw-h-11 tw-px-3 tw-border ${
                    formik.touched.email && formik.errors.email
                      ? "tw-border-red-500"
                      : "tw-border-[#D5D7DA]"
                  } tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500`}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                    {formik.errors.email}
                  </div>
                )}
              </div>

              <div className="tw-mb-6 form-group">
                <DynamicAutocomplete
                  name="userType"
                  label="User Type (Tier)"
                  options={userTypes}
                  selectedValue={formik.values.userType}
                  labelKey="user_type"
                  valueKey="id"
                  onChange={(value: any) => {
                    console.log("value: ", value);
                    formik.setFieldValue("userType", value);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.userType ? formik.errors.userType : undefined
                  }
                  touched={formik.touched.userType}
                  loading={isLoadingUserTypes}
                  required
                  placeholder="Select user type (tier)"
                  maxLengthMenu={45}
                />
              </div>
            </div>

            <div className="tw-mt-auto">
              <button
                type="submit"
                className="tw-w-full tw-h-11 tw-bg-[#146C94] tw-text-white tw-px-4 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#146C94] focus:tw-ring-offset-2 tw-transition-colors disabled:tw-opacity-50"
                disabled={formik.isSubmitting}
              >
                {isLoading ? "Inviting..." : "Invite Stakeholder"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserManagementInvite;
